import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {apiErrorMessage} from '../lib/apiError';
import {
  adminWordlistDeleteApiSelector,
  adminWordlistDetailApiSelector,
  adminWordlistDetailSelector,
  adminWordlistSaveApiSelector,
} from '../store/selectors/admin.selectors';
import {
  deleteAdminWordlist,
  fetchAdminWordlistDetail,
  updateAdminWordlist,
} from '../store/slices/adminWordlistsThunks';
import {clearAdminWordlistDetail} from '../store/slices/adminSlice';
import {DeleteConfirmationModal} from './DeleteConfirmationModal';
import {ErrorState, Field, FieldList, LoadingState, Panel, Timestamp, formatCount} from './admin/AdminPrimitives';
import {ArrowRightIcon, GlobeIcon, RefreshIcon, StarIcon, TrashIcon} from './Icons';

const controlClass = 'w-full rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2 text-sm '
  + 'text-gray-100 placeholder-gray-600 focus:border-indigo-500 focus:outline-none '
  + 'focus:ring-1 focus:ring-indigo-500 disabled:opacity-50';

/**
 * One word list's admin page: edit any field regardless of owner, see who owns it, and soft-delete
 * or restore it.
 *
 * Save and delete/restore both **refetch the detail** on success rather than patching the store from
 * the write response — the same choice `adminUserDetail.container.tsx` makes for the resend action.
 * The write endpoints return the raw DB row with `words` as an unparsed newline string and no
 * `owner`, so refetching is simpler than reassembling the detail shape from two different responses.
 */
const AdminWordlistDetailContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const {wordlistId} = useParams<{wordlistId: string}>();

  const wordlist = useAppSelector(adminWordlistDetailSelector);
  const detailApi = useAppSelector(adminWordlistDetailApiSelector);
  const saveApi = useAppSelector(adminWordlistSaveApiSelector);
  const deleteApi = useAppSelector(adminWordlistDeleteApiSelector);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [wordsText, setWordsText] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isOfficial, setIsOfficial] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Wipe the previous list before anything renders under the new id — the same reasoning as
  // `adminUserDetail.container.tsx` clearing the user profile on an id change.
  useEffect(() => {
    dispatch(clearAdminWordlistDetail());
  }, [dispatch, wordlistId]);

  useEffect(() => {
    if (wordlistId) {
      dispatch(fetchAdminWordlistDetail(wordlistId));
    }
  }, [dispatch, wordlistId]);

  // Seed the editable fields whenever a fresh copy of the list arrives — including after a save,
  // so a field nobody touched (say, the words another edit just changed) doesn't silently revert.
  useEffect(() => {
    if (!wordlist) {
      return;
    }
    setName(wordlist.name);
    setDescription(wordlist.description ?? '');
    setWordsText(wordlist.words.join('\n'));
    setIsPublic(wordlist.isPublic);
    setIsOfficial(wordlist.isOfficial);
  }, [wordlist]);

  if (!wordlistId) {
    return <ErrorState message="No word list id in the URL." />;
  }

  if (!wordlist && detailApi.status === 'pending') {
    return <LoadingState message="Loading this word list…" />;
  }

  if (!wordlist) {
    return (
      <ErrorState
        message={detailApi.error ?? 'This word list could not be loaded.'}
        onRetry={() => dispatch(fetchAdminWordlistDetail(wordlistId))}
      />
    );
  }

  const words = wordsText.split('\n').map(word => word.trim()).filter(word => word.length > 0);
  const isDirty = name.trim() !== wordlist.name
    || description.trim() !== (wordlist.description ?? '')
    || words.join('\n') !== wordlist.words.join('\n')
    || isPublic !== wordlist.isPublic
    || isOfficial !== wordlist.isOfficial;
  const canSave = isDirty && name.trim().length > 0 && saveApi.status !== 'pending';

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    try {
      await dispatch(updateAdminWordlist({
        id: wordlistId,
        name: name.trim(),
        description: description.trim(),
        words,
        isPublic,
        isOfficial,
      })).unwrap();

      showToast('Word list updated', 'success');
    } catch (error) {
      showToast(apiErrorMessage(error, 'Could not update this word list'), 'error');
    } finally {
      dispatch(fetchAdminWordlistDetail(wordlistId));
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteAdminWordlist(wordlistId)).unwrap();
      showToast(`Deleted “${wordlist.name}”`, 'success');
    } catch (error) {
      showToast(apiErrorMessage(error, 'Could not delete this word list'), 'error');
    } finally {
      setIsDeleteOpen(false);
      dispatch(fetchAdminWordlistDetail(wordlistId));
    }
  };

  const handleRestore = async () => {
    try {
      await dispatch(updateAdminWordlist({id: wordlistId, isDeleted: false})).unwrap();
      showToast(`Restored “${wordlist.name}”`, 'success');
    } catch (error) {
      showToast(apiErrorMessage(error, 'Could not restore this word list'), 'error');
    } finally {
      dispatch(fetchAdminWordlistDetail(wordlistId));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Link to="/admin/wordlists" className="text-indigo-400 hover:text-indigo-300">
            Word lists
          </Link>
          <ArrowRightIcon className="h-3 w-3" />
          <span className="text-gray-300">{wordlist.name}</span>
        </div>

        <button
          type="button"
          onClick={() => dispatch(fetchAdminWordlistDetail(wordlistId))}
          disabled={detailApi.status === 'pending'}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1
           text-[11px] font-medium text-gray-300 transition-colors hover:border-gray-600
           hover:bg-gray-800 disabled:opacity-40 cursor-pointer"
        >
          <RefreshIcon className={`h-3 w-3 ${detailApi.status === 'pending' ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {wordlist.isDeleted && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border
         border-red-500/40 bg-red-500/10 px-4 py-3">
          <p className="flex items-center gap-1.5 text-xs text-red-200">
            <TrashIcon className="h-3.5 w-3.5" />
            This list is soft-deleted — hidden from its owner and the marketplace.
          </p>
          <button
            type="button"
            onClick={handleRestore}
            disabled={saveApi.status === 'pending'}
            className="rounded-lg border border-red-400/40 px-2.5 py-1 text-[11px] font-medium
             text-red-100 transition-colors hover:bg-red-500/20 disabled:opacity-40 cursor-pointer"
          >
            Restore
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Details" className="lg:col-span-2">
          <div className="space-y-4">
            <div>
              <label htmlFor="detail-name" className="mb-1 block text-xs font-medium text-gray-300">
                Name
              </label>
              <input
                id="detail-name"
                type="text"
                value={name}
                onChange={event => setName(event.target.value)}
                className={controlClass}
              />
            </div>

            <div>
              <label htmlFor="detail-description" className="mb-1 block text-xs font-medium text-gray-300">
                Description
              </label>
              <textarea
                id="detail-description"
                value={description}
                onChange={event => setDescription(event.target.value)}
                rows={2}
                className={`${controlClass} resize-none`}
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-300">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={event => setIsPublic(event.target.checked)}
                  className="h-3.5 w-3.5 accent-indigo-500"
                />
                <GlobeIcon className="h-3.5 w-3.5 text-emerald-400" />
                Public
              </label>
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-300">
                <input
                  type="checkbox"
                  checked={isOfficial}
                  onChange={event => setIsOfficial(event.target.checked)}
                  className="h-3.5 w-3.5 accent-indigo-500"
                />
                <StarIcon className="h-3.5 w-3.5 text-amber-400" />
                Official
              </label>
            </div>

            <div>
              <div className="mb-1 flex items-baseline justify-between">
                <label htmlFor="detail-words" className="text-xs font-medium text-gray-300">
                  Words <span className="font-normal text-gray-600">(one per line)</span>
                </label>
                <span className="text-xs tabular-nums text-gray-600">
                  {words.length} word{words.length === 1 ? '' : 's'}
                </span>
              </div>
              <textarea
                id="detail-words"
                value={wordsText}
                onChange={event => setWordsText(event.target.value)}
                rows={12}
                className={`${controlClass} resize-y font-mono`}
              />
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-gray-800 pt-3">
              <button
                type="button"
                onClick={() => setIsDeleteOpen(true)}
                disabled={wordlist.isDeleted || deleteApi.status === 'pending'}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-red-400
                 transition-colors hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed
                 cursor-pointer"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                Delete
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
                 hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed
                 cursor-pointer"
              >
                {saveApi.status === 'pending' ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </Panel>

        <div className="space-y-4">
          <Panel title="Owner">
            {wordlist.owner ? (
              <FieldList>
                <Field label="Name">{wordlist.owner.name || '—'}</Field>
                <Field label="Email">{wordlist.owner.email}</Field>
                <Field label="Open">
                  <Link
                    to={`/admin/users/${wordlist.owner.id}`}
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    User page
                  </Link>
                </Field>
              </FieldList>
            ) : (
              <p className="text-xs text-gray-500">
                No owner record — the user row is missing or was itself deleted.
              </p>
            )}
          </Panel>

          <Panel title="Metadata">
            <FieldList>
              <Field label="Id" mono>{wordlist.id}</Field>
              <Field label="Words">{formatCount(wordlist.wordCount)}</Field>
              <Field label="Created"><Timestamp iso={wordlist.createdAt} /></Field>
              <Field label="Updated"><Timestamp iso={wordlist.updatedAt} /></Field>
            </FieldList>
          </Panel>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        listName={wordlist.name}
        isDeleting={deleteApi.status === 'pending'}
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteOpen(false)}
        title="Delete word list?"
        message="Are you sure you want to delete"
      />
    </div>
  );
};

export default AdminWordlistDetailContainer;
